import React, { useEffect, useState } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Paper,
  CircularProgress,
  TextField,
  InputAdornment,
  Chip,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Stack,
  IconButton,
  Tooltip,
  Alert,
  Avatar,
  CardHeader,
  Divider,
  InputAdornment as MuiInputAdornment
} from "@mui/material";
import {
  Search,
  FilterList,
  Refresh,
  Download,
  Visibility,
  History,
  TrendingUp,
  Receipt,
  Inventory,
  LocalShipping,
  Business,
  Schedule
} from "@mui/icons-material";
import { useSnackbar } from "notistack";
import { getAllLinenTransactions } from "../services/api";

// Enhanced StatsCard component
const StatsCard = ({ title, value, color = "primary", icon, ...props }) => (
  <Card 
    {...props}
    sx={{ 
      borderRadius: 3,
      boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
      background: `linear-gradient(135deg, ${color}.main 0%, ${color}.dark 100%)`,
      color: 'white',
      transition: 'all 0.3s ease',
      '&:hover': {
        transform: 'translateY(-4px)',
        boxShadow: '0 12px 48px rgba(0,0,0,0.15)'
      },
      ...props.sx
    }}
  >
    <CardContent sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box>
          <Typography 
            variant="h6" 
            sx={{ 
              fontSize: '1rem',
              fontWeight: 600,
              opacity: 0.9,
              mb: 1
            }}
          >
            {title}
          </Typography>
          <Typography 
            variant="h2" 
            sx={{ 
              fontSize: { xs: '2.5rem', md: '3rem' },
              fontWeight: 800,
              lineHeight: 1
            }}
          >
            {value}
          </Typography>
        </Box>
        <Box sx={{ opacity: 0.8 }}>
          {React.cloneElement(icon, { sx: { fontSize: '3rem' } })}
        </Box>
      </Box>
    </CardContent>
  </Card>
);

export const LinenHistory = () => {
  const { enqueueSnackbar } = useSnackbar();
  const [loading, setLoading] = useState(true);
  const [txns, setTxns] = useState([]);
  const [filteredTxns, setFilteredTxns] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [typeFilter, setTypeFilter] = useState("all");
  const [dateFilter, setDateFilter] = useState("all");
  const [sortBy, setSortBy] = useState("createdAt");
  const [sortOrder, setSortOrder] = useState("desc");

  // Transaction type options
  const transactionTypes = [
    "all",
    "issue",
    "return",
    "dispatch",
    "receive",
    "adjustment"
  ];

  // Date filter options
  const dateFilters = [
    { value: "all", label: "📅 All Time" },
    { value: "today", label: "📅 Today" },
    { value: "week", label: "📅 Last 7 Days" },
    { value: "month", label: "📅 Last 30 Days" }
  ];

  useEffect(() => {
    loadTransactions();
  }, []);

  useEffect(() => {
    filterAndSortTransactions();
  }, [txns, searchTerm, typeFilter, dateFilter, sortBy, sortOrder]);

  const loadTransactions = async () => {
    try {
      setLoading(true);
      const res = await getAllLinenTransactions();
      setTxns(res.data.transactions || res.data || []);
      enqueueSnackbar("✅ Transaction history loaded successfully", { variant: "success" });
    } catch (err) {
      console.error("Load transactions error:", err);
      enqueueSnackbar("❌ Failed to load transaction history", { variant: "error" });
    } finally {
      setLoading(false);
    }
  };

  const filterAndSortTransactions = () => {
    let filtered = [...txns];

    // Search filter
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(txn =>
        (txn.itemName && txn.itemName.toLowerCase().includes(term)) ||
        (txn.type && txn.type.toLowerCase().includes(term)) ||
        (txn.performedByName && txn.performedByName.toLowerCase().includes(term)) ||
        (txn.notes && txn.notes.toLowerCase().includes(term))
      );
    }

    // Type filter
    if (typeFilter !== "all") {
      filtered = filtered.filter(txn => txn.type === typeFilter);
    }

    // Date filter
    if (dateFilter !== "all") {
      const now = new Date();
      let startDate = new Date();

      switch (dateFilter) {
        case "today":
          startDate.setHours(0, 0, 0, 0);
          break;
        case "week":
          startDate.setDate(now.getDate() - 7);
          break;
        case "month":
          startDate.setDate(now.getDate() - 30);
          break;
        default:
          break;
      }

      filtered = filtered.filter(txn => {
        const txnDate = new Date(txn.createdAt);
        return txnDate >= startDate;
      });
    }

    // Sorting
    filtered.sort((a, b) => {
      let aValue = a[sortBy];
      let bValue = b[sortBy];

      if (sortBy === "createdAt") {
        aValue = new Date(aValue);
        bValue = new Date(bValue);
      }

      if (sortOrder === "desc") {
        return bValue - aValue;
      } else {
        return aValue - aValue;
      }
    });

    setFilteredTxns(filtered);
  };

  const getTypeColor = (type) => {
    const colors = {
      issue: "error",
      return: "success",
      dispatch: "warning",
      receive: "info",
      adjustment: "secondary"
    };
    return colors[type] || "default";
  };

  const getTypeLabel = (type) => {
    const labels = {
      issue: "📤 Issue",
      return: "📥 Return",
      dispatch: "🚚 Dispatch",
      receive: "✅ Receive",
      adjustment: "⚙️ Adjustment"
    };
    return labels[type] || type;
  };

  const getTypeIcon = (type) => {
    const icons = {
      issue: <Inventory />,
      return: <Receipt />,
      dispatch: <LocalShipping />,
      receive: <Business />,
      adjustment: <TrendingUp />
    };
    return icons[type] || <History />;
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffTime = Math.abs(now - date);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays === 1) {
      return "Yesterday";
    } else if (diffDays <= 7) {
      return `${diffDays} days ago`;
    } else {
      return date.toLocaleDateString();
    }
  };

  const handleExport = () => {
    // Simple CSV export
    const headers = ["Date", "Type", "Item", "Quantity", "Reference", "Performed By", "Notes"];
    const csvData = filteredTxns.map(txn => [
      new Date(txn.createdAt).toLocaleString(),
      txn.type,
      txn.itemName || txn.item,
      txn.quantity,
      txn.reference || "-",
      txn.performedByName || txn.performedBy || "-",
      txn.notes || "-"
    ]);

    const csvContent = [headers, ...csvData]
      .map(row => row.map(field => `"${field}"`).join(","))
      .join("\n");

    const blob = new Blob([csvContent], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `linen-history-${new Date().toISOString().slice(0, 10)}.csv`;
    link.click();
    URL.revokeObjectURL(url);

    enqueueSnackbar("📊 Data exported successfully", { variant: "success" });
  };

  const getSummaryStats = () => {
    const total = filteredTxns.length;
    const types = filteredTxns.reduce((acc, txn) => {
      acc[txn.type] = (acc[txn.type] || 0) + 1;
      return acc;
    }, {});

    return { total, types };
  };

  const stats = getSummaryStats();

  if (loading) {
    return (
      <Box sx={{ 
        display: "flex", 
        justifyContent: "center", 
        alignItems: "center", 
        minHeight: 400,
        flexDirection: 'column',
        gap: 3
      }}>
        <CircularProgress size={60} thickness={4} />
        <Box sx={{ textAlign: 'center' }}>
          <Typography variant="h3" sx={{ fontSize: '1.8rem', fontWeight: 600, mb: 1 }}>
            Loading Transaction History...
          </Typography>
          <Typography variant="h6" sx={{ fontSize: '1.2rem', color: 'text.secondary' }}>
            Preparing your transaction records
          </Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 4, backgroundColor: 'grey.50', minHeight: '100vh' }}>
      {/* Enhanced Header */}
      <Card sx={{ 
        mb: 4, 
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
        borderRadius: 3
      }}>
        <CardContent sx={{ p: 4 }}>
          <Grid container alignItems="center" spacing={3}>
            <Grid item>
              <Avatar
                sx={{
                  width: 80,
                  height: 80,
                  backgroundColor: 'rgba(255,255,255,0.2)',
                  backdropFilter: 'blur(10px)'
                }}
              >
                <History sx={{ fontSize: 48, color: 'white' }} />
              </Avatar>
            </Grid>
            <Grid item xs>
              <Typography 
                variant="h2" 
                fontWeight={800}
                sx={{ 
                  color: 'white',
                  fontSize: { xs: '2.5rem', md: '3rem' },
                  mb: 1
                }}
              >
                Linen Transaction History
              </Typography>
              <Typography 
                variant="h6" 
                sx={{ 
                  color: 'white', 
                  opacity: 0.9,
                  fontSize: '1.3rem'
                }}
              >
                Complete audit trail of all linen movements and activities
              </Typography>
            </Grid>
            <Grid item>
              <Stack direction="row" spacing={2}>
                <Tooltip title="📥 Export to CSV">
                  <Button
                    startIcon={<Download sx={{ fontSize: 28 }} />}
                    onClick={handleExport}
                    disabled={filteredTxns.length === 0}
                    variant="contained"
                    sx={{ 
                      fontSize: '1.1rem',
                      fontWeight: 600,
                      px: 3,
                      py: 1.5,
                      borderRadius: 3,
                      backgroundColor: 'white',
                      color: 'primary.main',
                      '&:hover': {
                        backgroundColor: 'grey.100',
                        transform: 'translateY(-2px)'
                      },
                      transition: 'all 0.3s ease',
                      '&:disabled': {
                        backgroundColor: 'grey.300'
                      }
                    }}
                  >
                    Export
                  </Button>
                </Tooltip>
                <Tooltip title="🔄 Refresh">
                  <IconButton 
                    onClick={loadTransactions}
                    sx={{ 
                      backgroundColor: 'white',
                      color: 'primary.main',
                      width: 56,
                      height: 56,
                      '&:hover': { 
                        backgroundColor: 'grey.100',
                        transform: 'rotate(180deg)'
                      },
                      transition: 'all 0.5s ease'
                    }}
                  >
                    <Refresh sx={{ fontSize: 28 }} />
                  </IconButton>
                </Tooltip>
              </Stack>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Enhanced Filters Card */}
      <Card sx={{ 
        mb: 4, 
        borderRadius: 3, 
        boxShadow: '0 8px 32px rgba(0,0,0,0.08)',
        background: 'linear-gradient(135deg, #fdfbfb 0%, #ebedee 100%)'
      }}>
        <CardHeader
          title={
            <Typography variant="h4" sx={{ fontSize: '1.6rem', fontWeight: 700 }}>
              🔍 Filter & Search Transactions
            </Typography>
          }
          sx={{
            borderBottom: '1px solid',
            borderColor: 'divider',
            backgroundColor: 'grey.50'
          }}
        />
        <CardContent sx={{ p: 4 }}>
          <Grid container spacing={3} alignItems="center">
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                placeholder="🔍 Search items, types, or users..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  sx: { 
                    fontSize: '1.1rem',
                    py: 1.5,
                    borderRadius: 3
                  },
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search sx={{ fontSize: 28, color: 'primary.main' }} />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 3,
                    backgroundColor: 'white'
                  }
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <FormControl fullWidth>
                <InputLabel sx={{ fontSize: '1.1rem' }}>📊 Type</InputLabel>
                <Select
                  value={typeFilter}
                  label="📊 Type"
                  onChange={(e) => setTypeFilter(e.target.value)}
                  sx={{ fontSize: '1.1rem', borderRadius: 3 }}
                >
                  {transactionTypes.map(type => (
                    <MenuItem key={type} value={type} sx={{ fontSize: '1.1rem' }}>
                      {type === "all" ? "📊 All Types" : getTypeLabel(type)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <FormControl fullWidth>
                <InputLabel sx={{ fontSize: '1.1rem' }}>📅 Period</InputLabel>
                <Select
                  value={dateFilter}
                  label="📅 Period"
                  onChange={(e) => setDateFilter(e.target.value)}
                  sx={{ fontSize: '1.1rem', borderRadius: 3 }}
                >
                  {dateFilters.map(filter => (
                    <MenuItem key={filter.value} value={filter.value} sx={{ fontSize: '1.1rem' }}>
                      {filter.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <FormControl fullWidth>
                <InputLabel sx={{ fontSize: '1.1rem' }}>🔄 Sort By</InputLabel>
                <Select
                  value={sortBy}
                  label="🔄 Sort By"
                  onChange={(e) => setSortBy(e.target.value)}
                  sx={{ fontSize: '1.1rem', borderRadius: 3 }}
                >
                  <MenuItem value="createdAt" sx={{ fontSize: '1.1rem' }}>📅 Date</MenuItem>
                  <MenuItem value="quantity" sx={{ fontSize: '1.1rem' }}>📦 Quantity</MenuItem>
                  <MenuItem value="type" sx={{ fontSize: '1.1rem' }}>📊 Type</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <FormControl fullWidth>
                <InputLabel sx={{ fontSize: '1.1rem' }}>📈 Order</InputLabel>
                <Select
                  value={sortOrder}
                  label="📈 Order"
                  onChange={(e) => setSortOrder(e.target.value)}
                  sx={{ fontSize: '1.1rem', borderRadius: 3 }}
                >
                  <MenuItem value="desc" sx={{ fontSize: '1.1rem' }}>🆕 Newest First</MenuItem>
                  <MenuItem value="asc" sx={{ fontSize: '1.1rem' }}>🕐 Oldest First</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Enhanced Summary Stats */}
      {filteredTxns.length > 0 && (
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} sm={6} md={3}>
            <StatsCard
              title="📊 Total Transactions"
              value={stats.total}
              color="primary"
              icon={<History />}
            />
          </Grid>
          {Object.entries(stats.types).map(([type, count]) => (
            <Grid item xs={12} sm={6} md={3} key={type}>
              <StatsCard
                title={getTypeLabel(type)}
                value={count}
                color={getTypeColor(type)}
                icon={getTypeIcon(type)}
              />
            </Grid>
          ))}
        </Grid>
      )}

      {/* Enhanced Transactions Table */}
      <Card sx={{ 
        borderRadius: 3, 
        boxShadow: '0 8px 32px rgba(0,0,0,0.08)',
        overflow: 'hidden'
      }}>
        <CardHeader
          title={
            <Typography variant="h4" sx={{ fontSize: '1.6rem', fontWeight: 700 }}>
              📋 Transaction Records
            </Typography>
          }
          action={
            <Stack direction="row" spacing={2} alignItems="center">
              {searchTerm || typeFilter !== "all" || dateFilter !== "all" ? (
                <Chip
                  label="🎯 Filters Active"
                  color="primary"
                  size="large"
                  onDelete={() => {
                    setSearchTerm("");
                    setTypeFilter("all");
                    setDateFilter("all");
                  }}
                  sx={{ fontSize: '1.1rem', fontWeight: 600 }}
                />
              ) : null}
              <Typography variant="h6" sx={{ fontSize: '1.3rem', fontWeight: 700, color: 'primary.main' }}>
                {filteredTxns.length} Records
              </Typography>
            </Stack>
          }
          sx={{
            borderBottom: '1px solid',
            borderColor: 'divider',
            backgroundColor: 'grey.50',
            py: 3
          }}
        />
        <CardContent sx={{ p: 0 }}>
          {filteredTxns.length === 0 ? (
            <Box sx={{ p: 6, textAlign: 'center' }}>
              <History sx={{ fontSize: '4rem', color: 'grey.400', mb: 3 }} />
              <Typography variant="h4" sx={{ fontSize: '1.8rem', fontWeight: 600, mb: 2, color: 'text.secondary' }}>
                {txns.length === 0 
                  ? "📭 No Transactions Found" 
                  : "🔍 No Transactions Match Your Filters"}
              </Typography>
              <Typography variant="h6" sx={{ fontSize: '1.2rem', color: 'text.secondary' }}>
                {txns.length === 0 
                  ? "Start creating transactions to see them here" 
                  : "Try adjusting your search criteria or filters"}
              </Typography>
            </Box>
          ) : (
            <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 0 }}>
              <Table>
                <TableHead sx={{ backgroundColor: 'primary.main' }}>
                  <TableRow>
                    <TableCell sx={{ color: 'white', fontWeight: 800, fontSize: '1.2rem', py: 3 }}>
                      📅 Date & Time
                    </TableCell>
                    <TableCell sx={{ color: 'white', fontWeight: 800, fontSize: '1.2rem', py: 3 }}>
                      📊 Type
                    </TableCell>
                    <TableCell sx={{ color: 'white', fontWeight: 800, fontSize: '1.2rem', py: 3 }}>
                      📦 Item
                    </TableCell>
                    <TableCell align="center" sx={{ color: 'white', fontWeight: 800, fontSize: '1.2rem', py: 3 }}>
                      🔢 Quantity
                    </TableCell>
                    <TableCell sx={{ color: 'white', fontWeight: 800, fontSize: '1.2rem', py: 3 }}>
                      🆔 Reference
                    </TableCell>
                    <TableCell sx={{ color: 'white', fontWeight: 800, fontSize: '1.2rem', py: 3 }}>
                      👤 Performed By
                    </TableCell>
                    <TableCell sx={{ color: 'white', fontWeight: 800, fontSize: '1.2rem', py: 3 }}>
                      📝 Notes
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredTxns.map((txn) => (
                    <TableRow 
                      key={txn._id} 
                      hover
                      sx={{ 
                        '&:hover': {
                          backgroundColor: 'primary.light',
                          transform: 'translateY(-1px)',
                          transition: 'all 0.3s ease'
                        },
                        transition: 'all 0.2s ease'
                      }}
                    >
                      <TableCell>
                        <Stack>
                          <Typography variant="body1" sx={{ fontSize: '1.1rem', fontWeight: 600 }}>
                            {new Date(txn.createdAt).toLocaleDateString()}
                          </Typography>
                          <Typography variant="body2" color="text.secondary" sx={{ fontSize: '1rem' }}>
                            {new Date(txn.createdAt).toLocaleTimeString()}
                          </Typography>
                        </Stack>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={getTypeLabel(txn.type)}
                          color={getTypeColor(txn.type)}
                          size="medium"
                          sx={{ fontSize: '1rem', fontWeight: 600 }}
                          icon={getTypeIcon(txn.type)}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body1" sx={{ fontSize: '1.1rem', fontWeight: 600 }}>
                          {txn.itemName || txn.item || "Unknown Item"}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Chip
                          label={txn.quantity}
                          variant="filled"
                          color="primary"
                          size="medium"
                          sx={{ fontSize: '1.1rem', fontWeight: 700 }}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body1" sx={{ fontSize: '1.1rem', fontWeight: 600, color: "text.secondary" }}>
                          {txn.reference ? `#${txn.reference.slice(-8)}` : "-"}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body1" sx={{ fontSize: '1.1rem', fontWeight: 600 }}>
                          {txn.performedByName || txn.performedBy || "System"}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Tooltip title={txn.notes || "No notes"} placement="top">
                          <Typography 
                            variant="body1" 
                            sx={{ 
                              fontSize: '1.1rem',
                              maxWidth: 200,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                              color: txn.notes ? "text.primary" : "text.secondary"
                            }}
                          >
                            {txn.notes || "-"}
                          </Typography>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      {/* Last Updated */}
      <Card sx={{ mt: 4, borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.08)' }}>
        <CardContent sx={{ p: 3, textAlign: 'center' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2 }}>
            <Schedule color="primary" />
            <Typography variant="h6" sx={{ fontSize: '1.1rem', fontWeight: 600, color: 'text.secondary' }}>
              Last updated: {new Date().toLocaleString()}
            </Typography>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};